import { Link } from "react-router-dom";
import { Instagram, Mail } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { api, CONTACT, CITIES } from "@/lib/api";

export default function Footer() {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const subscribe = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            await api.post("/newsletter", { email });
            toast.success("Subscribed. Welcome to the Aqualyx journal.");
            setEmail("");
        } catch (err) {
            toast.error("Subscription failed. Please try again.");
        }
        setLoading(false);
    };

    return (
        <footer data-testid="footer" className="bg-ink text-bone mt-20">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5 space-y-8">
                        <div>
                            <div className="eyebrow text-champagne mb-4">Aqualyx New Zealand</div>
                            <h3 className="font-serif text-5xl md:text-6xl leading-none tracking-tight">
                                The original<br />injectable fat<br />reduction.
                            </h3>
                        </div>
                        <p className="text-bone/60 max-w-md text-sm leading-relaxed">
                            Available only through Aqualyx Certified Clinics across New Zealand. Medsafe registered. Trusted internationally for over 15 years.
                        </p>
                        <form onSubmit={subscribe} className="flex border border-champagne/40 max-w-md" data-testid="newsletter-form">
                            <input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Your email"
                                data-testid="newsletter-email"
                                className="flex-1 bg-transparent px-4 py-3 text-sm placeholder:text-bone/40 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="newsletter-submit"
                                className="px-6 bg-champagne text-ink text-[0.65rem] uppercase tracking-[0.2em] font-medium hover:bg-bone transition-colors"
                            >
                                {loading ? "..." : "Subscribe"}
                            </button>
                        </form>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="eyebrow text-champagne mb-4">Explore</div>
                        <ul className="space-y-3 text-sm text-bone/70">
                            <li><Link to="/" className="hover:text-champagne">Treatment</Link></li>
                            <li><Link to="/results" className="hover:text-champagne">Results</Link></li>
                            <li><Link to="/clinics" className="hover:text-champagne">Clinics</Link></li>
                            <li><Link to="/blog" className="hover:text-champagne">Journal</Link></li>
                            <li><Link to="/about" className="hover:text-champagne">About</Link></li>
                            <li><Link to="/contact" className="hover:text-champagne">Contact</Link></li>
                        </ul>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="eyebrow text-champagne mb-4">Cities</div>
                        <ul className="space-y-3 text-sm text-bone/70">
                            {CITIES.map((c) => (
                                <li key={c}>
                                    <Link to={`/city/${c.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-champagne">
                                        {c}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="lg:col-span-3 space-y-6">
                        <div>
                            <div className="eyebrow text-champagne mb-4">Contact</div>
                            <a
                                href={`mailto:${CONTACT.email}`}
                                className="flex items-center gap-2 text-sm hover:text-champagne"
                                data-testid="footer-email"
                            >
                                <Mail strokeWidth={1.2} size={16} /> {CONTACT.email}
                            </a>
                        </div>
                        <div className="flex gap-4">
                            <a href="https://www.instagram.com/aqualyx_nz/" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="text-bone/60 hover:text-champagne transition-colors">
                                <Instagram strokeWidth={1.2} />
                            </a>
                        </div>
                        <div className="text-[0.65rem] uppercase tracking-[0.2em] text-bone/50 leading-relaxed border-t border-champagne/20 pt-6">
                            Aqualyx™ certified providers only
                        </div>
                    </div>
                </div>

                <div className="border-t border-bone/10 mt-16 pt-10 grid grid-cols-1 md:grid-cols-2 gap-6 text-xs text-bone/40">
                    <p className="leading-relaxed">
                        Aqualyx™ injection non-surgically reduces localized fat deposits. Aqualyx™ is a Class III medical device. Aqualyx™ has risks and benefits. Consult your healthcare professional to determine if Aqualyx™ is right for you. Medsafe registered in New Zealand. Individual results may vary.
                    </p>
                    <p className="md:text-right">
                        © {new Date().getFullYear()} Aqualyx New Zealand. All rights reserved.
                    </p>
                </div>
            </div>
        </footer>
    );
}
